/**
 * Client invoice address helpers.
 *
 * The `/clients` list endpoint returns a lightweight record without the
 * structured billing address — that lives in the Financials system and is
 * only exposed via `/clients/{id}/financials`.
 */

import { fetchClientFinancials } from '@/lib/data-service';
import type { Address, Client } from '@/types';

/** Format a client billing address as the single comma-separated line used on invoices. */
export function formatClientInvoiceAddress(address?: Address): string {
  if (!address) return '';
  return [
    address.line1,
    address.line2,
    address.city,
    address.county,
    address.postcode,
  ]
    .filter(Boolean)
    .join(', ');
}

/**
 * Resolve a client's formatted billing address, fetching the Financials detail
 * when the client record does not already carry it. Returns an empty string
 * when the address cannot be resolved.
 */
export async function resolveClientInvoiceAddress(
  client: Pick<Client, 'id' | 'invoiceAddress'> | null | undefined,
  companyId?: number | string | null,
): Promise<string> {
  if (!client) return '';
  if (client.invoiceAddress) return formatClientInvoiceAddress(client.invoiceAddress);
  if (companyId == null) return '';
  try {
    const details = await fetchClientFinancials(client.id, Number(companyId));
    return formatClientInvoiceAddress(details.invoiceAddress);
  } catch {
    return '';
  }
}
